import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Button, Text } from '@chakra-ui/react';
import { ChevronLeft } from 'lucide-react';
import { ProfileDropdown } from '@/components/ui/ProfileDropdown/ProfileDropdown';
import { pillTitleStyles } from './pillTitleStyles';

export interface PageHeaderProps {
  title?: string;
  showBack?: boolean;
  onBack?: () => void;
  backLabel?: string;
  showMobileLogo?: boolean;
  leftSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
  profileDropdownProps?: { showRewards?: boolean; onRewardsClick?: () => void };
  subtitle?: string;
}

export function PageHeader({
  title,
  showBack = false,
  onBack,
  backLabel = 'Back',
  showMobileLogo = true,
  leftSlot,
  rightSlot,
  profileDropdownProps,
  subtitle,
}: PageHeaderProps) {
  const navigate = useNavigate();
  const [showLogo, setShowLogo] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const fn = () => setShowLogo(!mq.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const logoMark = showLogo && showMobileLogo && !showBack ? (
    <Box display="block" flexShrink={0}>
      <img
        src="/assets/images/volyx-mark.png"
        alt="Volyx"
        style={{ width: 40, height: 40, objectFit: 'contain' }}
      />
    </Box>
  ) : null;

  const leftContent =
    leftSlot ??
    (showBack ? (
      <Flex alignItems="center" gap={2}>
        {logoMark}
        <Button
          variant="ghost"
          size="sm"
          gap={2}
          color="volyx.primary"
          fontSize="md"
          p={2}
          borderRadius="lg"
          _hover={{ bg: 'volyx.lightGray' }}
          onClick={handleBack}
        >
          <ChevronLeft size={24} />
          {backLabel}
        </Button>
      </Flex>
    ) : logoMark);

  const centerContent = title ? (
    <Box
      {...pillTitleStyles}
      fontSize={{ base: 'lg', md: 'xl' }}
      textAlign="center"
      justifySelf="center"
      minW={0}
      maxW="100%"
      truncate
    >
      {title}
    </Box>
  ) : subtitle ? (
    <Box minW={0} px={1}>
      <Text fontSize="sm" color="volyx.darkGray" m={0}>
        {subtitle}
      </Text>
    </Box>
  ) : null;

  const hasCenterContent = centerContent !== null;

  return (
    <Box
      as="header"
      display="grid"
      gridTemplateColumns={
        hasCenterContent
          ? { base: 'minmax(0, 1fr) auto', lg: 'minmax(0, 1fr) auto minmax(0, 1fr)' }
          : 'minmax(0, 1fr) auto'
      }
      alignItems="center"
      gap={{ base: 3, md: 4 }}
      py={{ base: 0, md: 2, lg: 4 }}
      mb={{ base: 5, md: 6 }}
      mt={{ base: 4, md: 0 }}
    >
      <Flex alignItems="center" minW={0} justify="flex-start" order={1}>
        {leftContent}
      </Flex>

      {centerContent && (
        <Flex
          alignItems="center"
          justify="center"
          minW={0}
          order={{ base: 3, lg: 2 }}
          gridColumn={{ base: '1 / -1', lg: 'auto' }}
        >
          {centerContent}
        </Flex>
      )}

      <Flex alignItems="center" justify="flex-end" minW={0} order={{ base: 2, lg: 3 }}>
        {rightSlot ?? <ProfileDropdown {...profileDropdownProps} />}
      </Flex>
    </Box>
  );
}
